import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Link } from "react-router-dom";

interface OptionType {
  value: number;
  label: string;
}

interface PaginationProps {
  currentPage: number;
  perPage: number;
  totalItems: number;
  perPageOptions: OptionType[];
  onPageChange: (page: number) => void;
  onPerPageChange: (value: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  perPage,
  totalItems,
  perPageOptions,
  onPageChange,
  onPerPageChange,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // On mobile, force perPage = 5 (only once when mounted or device type changes)
  useEffect(() => {
    if (isMobile && perPage !== 5) {
      onPerPageChange(5);
    }
  }, [isMobile]);

  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const visiblePages = 3;

  const getPageNumbers = () => {
    const pages: number[] = [];
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(start + visiblePages - 1, totalPages);

    if (end - start < visiblePages - 1) {
      start = Math.max(1, end - visiblePages + 1);
    }

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="col-xl-12">
      <div className="card mb-0 border-top-0">
        <div className="card-body py-3">
          <nav className="pagination-style-3 d-flex justify-content-end align-items-center gap-2">
            <Select
              classNamePrefix="react-select"
              options={perPageOptions}
              className="select w-auto"
              value={perPageOptions.find((opt) => opt.value === perPage)}
              onChange={(selected) =>
                selected && onPerPageChange(selected.value)
              }
              isSearchable={false}
              menuPlacement="auto"
            />

            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <Link
                  className="page-link"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) onPageChange(currentPage - 1);
                  }}
                >
                  Prev
                </Link>
              </li>

              {getPageNumbers().map((num) => (
                <li
                  key={num}
                  className={`page-item ${num === currentPage ? "active" : ""}`}
                >
                  <Link
                    className="page-link"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onPageChange(num);
                    }}
                  >
                    {num}
                  </Link>
                </li>
              ))}

              {currentPage + 1 < totalPages && (
                <>
                  <li className="page-item disabled">
                    <span className="page-link">…</span>
                  </li>
                  <li className="page-item">
                    <Link
                      className="page-link"
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(totalPages);
                      }}
                    >
                      {totalPages}
                    </Link>
                  </li>
                </>
              )}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <Link
                  className="page-link"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage < totalPages) onPageChange(currentPage + 1);
                  }}
                >
                  Next
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
